import { useAppSelector } from 'app/hooks';
import { useEffect, useRef } from 'react';
import {
  refetchInstance,
  refetchInstanceConfigurations,
  refetchInstanceSettings,
  refetchInstanceSystems,
  refetchProject,
  refetchProjectAttachments,
  refetchProjectDetails,
  refetchStructureAttachments,
  refetchStructureLevels,
  refetchStructureMetadata,
  refetchStructureSpace
} from 'utils/data';
import {
  deferRefetchUntilDragEnds,
  isLinesetDragActive
} from 'utils/lineset-drag-state';

type RefetchTarget =
  | 'structure'
  | 'structure:space'
  | 'structure:section'
  | 'structure:level'
  | 'structure:metadata'
  | 'structure:images'
  | 'instance'
  | 'instance:configurations'
  | 'instance:systems'
  | 'instance:settings'
  | 'project'
  | 'project:details'
  | 'project:attachments'
  | 'configuration'
  | 'none';

interface SSEPayload {
  tool: string;
  result: any;
  timestamp: string;
  refetchTarget: RefetchTarget;
  refetchParams?: {
    structureUniqueId?: string;
    configurationUniqueId?: string;
    projectId?: number;
    spaceUniqueId?: string;
    sectionUniqueId?: string;
    levelUniqueId?: string;
    changedFields?: string[];
  };
}

async function handleRefetch(payload: SSEPayload) {
  const { refetchTarget, refetchParams } = payload;

  switch (refetchTarget) {
    case 'structure':
      await refetchStructureLevels(refetchParams?.structureUniqueId);
      break;
    case 'structure:space':
      if (refetchParams?.spaceUniqueId) {
        await refetchStructureSpace(
          refetchParams.spaceUniqueId,
          refetchParams.structureUniqueId
        );
      } else {
        await refetchStructureLevels(refetchParams?.structureUniqueId);
      }
      break;
    case 'structure:section':
    case 'structure:level':
      await refetchStructureLevels(refetchParams?.structureUniqueId);
      break;
    case 'structure:metadata':
      await refetchStructureMetadata(refetchParams?.structureUniqueId);
      break;
    case 'structure:images':
      await refetchStructureAttachments(refetchParams?.structureUniqueId);
      break;
    case 'instance':
      await refetchInstance();
      break;
    case 'instance:configurations':
    case 'configuration':
      if (isLinesetDragActive()) {
        deferRefetchUntilDragEnds('instance:configurations', () =>
          refetchInstanceConfigurations()
        );
      } else {
        await refetchInstanceConfigurations();
      }
      break;
    case 'instance:systems':
      await refetchInstanceSystems();
      break;
    case 'instance:settings':
      await refetchInstanceSettings();
      break;
    case 'project':
      await refetchProject(refetchParams?.projectId);
      break;
    case 'project:details':
      await refetchProjectDetails(refetchParams?.projectId);
      break;
    case 'project:attachments':
      await refetchProjectAttachments(refetchParams?.projectId);
      break;
    case 'none':
    default:
      break;
  }
}

export default function SSEWatcher() {
  const { structureUniqueId } = useAppSelector((state) => state.structure);
  const { projectDetails } = useAppSelector((state) => state.project);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const reconnectBackoffMsRef = useRef(1000);

  useEffect(() => {
    const sseUrl = `${process.env.REACT_APP_SYSTEM_SELECTOR_API_URL}sse`;

    const connect = () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }

      const eventSource = new EventSource(sseUrl, { withCredentials: true });
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        console.log('SSE: Connected');
        reconnectBackoffMsRef.current = 1000;
      };

      eventSource.addEventListener('goal-completed', (event) => {
        try {
          const payload = JSON.parse(event.data) as SSEPayload;

          if (
            payload.refetchParams?.structureUniqueId &&
            payload.refetchParams.structureUniqueId !== structureUniqueId
          ) {
            return;
          }

          if (
            payload.refetchParams?.projectId &&
            payload.refetchParams.projectId !== projectDetails?.projectId
          ) {
            return;
          }

          handleRefetch(payload);
        } catch (error) {
          console.error('SSE: Failed to parse event data', error);
        }
      });

      eventSource.onerror = () => {
        console.warn('SSE: Connection error, attempting to reconnect...');
        eventSource.close();
        eventSourceRef.current = null;

        const baseDelayMs = Math.min(30000, reconnectBackoffMsRef.current);
        reconnectBackoffMsRef.current = Math.min(
          30000,
          reconnectBackoffMsRef.current * 2
        );
        const jitterMs = Math.floor(Math.random() * 1001) - 500;
        const delayMs = Math.max(0, baseDelayMs + jitterMs);

        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, delayMs);
      };
    };

    connect();

    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [structureUniqueId, projectDetails?.projectId]);

  return null;
}
